import React, { FC, useCallback, useState, useEffect } from "react";

import {
    Button,
    EmailInput,
    LoadingSpinner,
    PageAlert,
    SmartDropdown,
    SmartDropdownOption,
    validateEmail,
} from "@xapp/components";

import { OnboardingProps } from "../models";

export interface SendLeadsStepProps {
    /**
     * If network activity is happening
     */
    loading?: boolean;
    /**
     * Friendly message to display to the user.
     */
    loadingMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string; message: string };
    /**
     * Disable all integrations & interactions (except "next" & "previous")
     */
    disabled?: boolean;
    /**
     * The app created in the onboarding that we are integrating to
     */
    app: OnboardingProps;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * When provided, it will imit the integrations to those in the
     */
    limitIntegrations?: string[];
    /**
     * Are integrations supported with the current tier
     */
    integrationsSupported?: boolean;
    /**
     * Upgrades the current plan to Leads
     */
    onUpgrade(): void;
    /**
     * User is ready to save everything!
     */
    onNext(app: Pick<OnboardingProps, "leadsContact" | "beta">): void;
    /**
     * User wants to go back to the previous step
     */
    onBack?(): void;
}

export const SendLeadsStep: FC<SendLeadsStepProps> = (props) => {
    const { app, style, onBack, onNext, error, className, loading, loadingMessage } = props;

    const [leadsContact, setLeadsContact] = useState<string>(app?.leadsContact || "");
    const [leadsContactValid, setLeadsContactValid] = useState<boolean>(validateEmail(app?.leadsContact || ""));

    const [selectedIntegration, setSelectedIntegration] = useState<string[]>([]);

    const fsmOptions: SmartDropdownOption[] = [
        {
            id: "none",
            name: "I don't use one",
        },
        {
            id: "fieldpulse",
            name: "FieldPulse",
        },
        {
            id: "housecallpro",
            name: "HousecallPro",
        },
        {
            id: "jobber",
            name: "Jobber",
        },
        {
            id: "surefire",
            name: "Surefire Local",
        },
        {
            id: "servicetitan",
            name: "ServiceTitan",
        },
        {
            id: "servicefusion",
            name: "Service Fusion",
        },
        {
            id: "workiz",
            name: "Workiz",
        },
        {
            id: "lacrm",
            name: "Less Annoying CRM",
        },
        {
            id: "salesforce",
            name: "Salesforce",
        },
        {
            id: "hubspot",
            name: "HubSpot",
        },
        {
            id: "other",
            name: "Other / Not Listed",
        },
    ];

    const handleFSMChange = useCallback((selectedOption: SmartDropdownOption) => {
        setSelectedIntegration([selectedOption.name]);
    }, []);

    useEffect(() => {
        if (app?.leadsContact) {
            setLeadsContact(app.leadsContact);
        }
    }, [app]);

    const handleLeadsContactUpdated = useCallback((value: string, valid: boolean) => {
        setLeadsContact(value);
        setLeadsContactValid(valid);
    }, []);

    const handleNextClicked = useCallback(() => {
        onNext({
            leadsContact,
            beta: {
                ...app?.beta,
                onboarding: {
                    ...app?.beta?.onboarding,
                    leadsIntegration: selectedIntegration,
                },
            },
        });
    }, [onNext, leadsContact, selectedIntegration, app?.beta]);

    const canProceed = leadsContact && leadsContactValid;

    return (
        <div style={{ ...style }} className={className}>
            {error ? (
                <PageAlert background="orange">
                    <p>{error.message}</p>
                    <pre style={{ overflow: "scroll" }}>{error.error}</pre>
                    {/**We need a call to action for them to get help */}
                </PageAlert>
            ) : (
                <></>
            )}
            {loading ? (
                <div
                    style={{
                        padding: "20px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <LoadingSpinner textStyle={{ fontSize: "1em" }} loadingText={loadingMessage} />
                </div>
            ) : (
                <>
                    <h2>What email can we send leads to?</h2>

                    <div>
                        <p>
                            You can access your leads anytime in Studio but we also need an email address to send them
                            to so you can get them immediately.
                        </p>

                        <EmailInput
                            variation="outlined"
                            label={"Leads Email"}
                            value={leadsContact}
                            onChange={handleLeadsContactUpdated}
                        />
                    </div>

                    <div>
                        <h4>Do you use Field Service Management (FSM) or Customer Relationship Management (CRM)?</h4>
                        <p>Starting on Leads+ plans, we can also send your leads directly to your FSM/CRM.</p>

                        <SmartDropdown
                            slugLabel={selectedIntegration[0] || "Select FSM/CRM"}
                            options={fsmOptions}
                            onChange={handleFSMChange}
                        />
                        <small>We will give you the opportunity to upgrade later if you like.</small>
                    </div>

                    <div
                        style={{
                            padding: "20px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: typeof onBack === "function" ? "space-between" : "right",
                        }}
                    >
                        {typeof onBack === "function" && (
                            <Button leftIcon="arrow_back" label={`Back`} variation="secondary" onClick={onBack} />
                        )}
                        <Button
                            rightIcon="arrow_forward"
                            label={`Next`}
                            variation="primary"
                            disabled={!canProceed}
                            onClick={handleNextClicked}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
